// PINE HISTORY (God 2026-10-12): the headline tiles count only the ACTIVE Pine v6 (enabled legs, since their current
// pin). Every FAIL-stamped / replaced Pine is closed here WITH its own win rate, trades and net - visible, never mixed
// into the headline numbers. Data: /reports/pine-history (Node /api/trades/pine-history).
import { useEffect, useState } from 'react';
import { API_BASE_URL } from './foundation';

type Row = { cell: string; epoch: 'ACTIVE' | 'CLOSED'; pineVer: number | null; pinnedAt: string | null; enabled: boolean;
             trades: number; wins: number; winRate: number; net: number; first: string | null; last: string | null;
             failStamp: { ver?: number; trades?: number; winRate?: number; net?: number; reasons?: string[]; judgedAt?: string } | null };
type Tot = { trades: number; wins: number; net: number };

const inr = (n: number) => `${n >= 0 ? '+' : '−'}₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`;
const day = (s: string | null) => (s ? s.slice(0, 10) : '—');

export default function PineHistoryPanel({ mode = 'PAPER' }: { mode?: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<{ rows: Row[]; active: Tot; closed: Tot } | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const pull = async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/reports/pine-history?mode=${encodeURIComponent(mode)}`, { signal: AbortSignal.timeout(12000) });
        const j = await r.json();
        if (!alive) return;
        if (r.ok && j && Array.isArray(j.rows)) { setData(j); setErr(''); } else setErr(j?.detail || j?.message || `HTTP ${r.status}`);
      } catch (e: any) { if (alive) setErr(String(e?.message || e)); }
    };
    pull();
    const id = setInterval(() => { if (!document.hidden) pull(); }, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [open, mode]);
  const wr = (t: Tot) => (t.trades ? `${((t.wins / t.trades) * 100).toFixed(1)}%` : '—');
  const closed = (data?.rows || []).filter(r => r.epoch === 'CLOSED');
  return (
    <div style={{ margin: '6px 0 10px', border: '1px solid #1e293b', borderRadius: 8, background: '#0b1220' }}>
      <button type="button" onClick={() => setOpen(v => !v)}
        style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer',
                 padding: '8px 12px', fontSize: 11, fontWeight: 800, letterSpacing: 0.4 }}>
        {open ? '▾' : '▸'} PINE HISTORY — headline tiles = ACTIVE Pine v6 only; FAIL / replaced Pines closed here with their own win rate
      </button>
      {open && (
        <div style={{ padding: '0 12px 10px' }}>
          {err && <div style={{ color: '#f87171', fontSize: 11 }}>Pine history load nahi hua: {err}</div>}
          {data && (
            <>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 11, color: '#94a3b8', margin: '4px 0 8px' }}>
                <span>🟢 ACTIVE Pine: <b style={{ color: '#e2e8f0' }}>{data.active.trades}</b> trades · win {wr(data.active)} · <b style={{ color: data.active.net >= 0 ? '#34d399' : '#f87171' }}>{inr(data.active.net)}</b></span>
                <span>🔒 CLOSED (FAIL / replaced): <b style={{ color: '#e2e8f0' }}>{data.closed.trades}</b> trades · win {wr(data.closed)} · <b style={{ color: data.closed.net >= 0 ? '#34d399' : '#f87171' }}>{inr(data.closed.net)}</b></span>
              </div>
              <div style={{ overflowX: 'auto', maxHeight: 320, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5, fontVariantNumeric: 'tabular-nums' }}>
                  <thead>
                    <tr style={{ color: '#64748b', textAlign: 'left' }}>
                      <th style={{ padding: '4px 6px' }}>Cell</th><th>Epoch</th><th>Pine</th><th>From → To</th>
                      <th style={{ textAlign: 'right' }}>Trades</th><th style={{ textAlign: 'right' }}>Win %</th>
                      <th style={{ textAlign: 'right' }}>Net</th><th style={{ paddingLeft: 8 }}>FAIL stamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...data.rows.filter(r => r.epoch === 'ACTIVE'), ...closed].map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #1e293b', color: r.epoch === 'ACTIVE' ? '#e2e8f0' : '#94a3b8' }}>
                        <td style={{ padding: '3px 6px', whiteSpace: 'nowrap' }}>{r.cell.replace(/\|/g, ' · ')}</td>
                        <td>{r.epoch === 'ACTIVE' ? '🟢 ACTIVE' : r.failStamp ? '🔴 FAIL' : '🔒 CLOSED'}</td>
                        <td>{r.epoch === 'ACTIVE' ? (r.pineVer != null ? `v${r.pineVer}` : '—') : (r.failStamp?.ver != null ? `v${r.failStamp.ver}` : 'older')}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{day(r.first)} → {day(r.last)}</td>
                        <td style={{ textAlign: 'right' }}>{r.trades}</td>
                        <td style={{ textAlign: 'right' }}>{r.winRate.toFixed(1)}</td>
                        <td style={{ textAlign: 'right', color: r.net >= 0 ? '#34d399' : '#f87171' }}>{inr(r.net)}</td>
                        <td style={{ paddingLeft: 8, maxWidth: 360, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={(r.failStamp?.reasons || []).join(' · ')}>
                          {r.failStamp ? `${r.failStamp.trades ?? '—'} tr · ${r.failStamp.winRate ?? '—'}% · ${(r.failStamp.reasons || []).join(' · ')}` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
